import { useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

/**
 * ExportMenu — reusable dropdown that downloads a report as CSV or PDF.
 *
 * Props:
 *  - endpoint: string   e.g. "/reports/sales", "/inventory/low-stock", "/expenses"
 *  - params: object     Same filters currently rendered on screen
 *  - permission: string e.g. "reports:export" | "analytics:export" | "expenses:export"
 *  - filename: string   filename stem (without extension), used only if server does not set Content-Disposition
 *  - label?: string     Optional custom label
 *  - testId?: string    Optional testid override
 */
export function ExportMenu({
  endpoint,
  params = {},
  permission,
  filename = "reporte",
  label = "Exportar",
  testId = "export-menu",
}) {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(null); // "csv" | "pdf" | null

  if (permission && !hasPermission(permission)) return null;

  const parseFilename = (headers, fallback) => {
    const cd = headers?.["content-disposition"] || headers?.["Content-Disposition"] || "";
    const match = /filename="?([^"]+)"?/.exec(cd);
    return match ? match[1] : fallback;
  };

  const doDownload = async (fmt) => {
    setLoading(fmt);
    try {
      const { data, headers } = await api.get(endpoint, {
        params: { ...params, format: fmt },
        responseType: "blob",
      });
      const blob = new Blob([data], {
        type: fmt === "csv" ? "text/csv;charset=utf-8" : "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = parseFilename(headers, `${filename}.${fmt}`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Descarga lista · ${fmt.toUpperCase()}`);
    } catch (err) {
      // Blob error responses need to be parsed as text first
      let msg = "No se pudo exportar";
      try {
        const txt = await err?.response?.data?.text?.();
        if (txt) {
          try { msg = JSON.parse(txt).detail || msg; } catch (_) { msg = txt; }
        }
      } catch (_) { /* keep default */ }
      toast.error(msg);
    } finally {
      setLoading(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          data-testid={testId}
          disabled={!!loading}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Descargar como
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => doDownload("csv")}
          disabled={!!loading}
          data-testid={`${testId}-csv`}
        >
          <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" />
          <div className="flex-1">
            <div className="text-sm">CSV</div>
            <div className="text-[10px] text-muted-foreground">Excel / Google Sheets</div>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => doDownload("pdf")}
          disabled={!!loading}
          data-testid={`${testId}-pdf`}
        >
          <FileText className="h-4 w-4 mr-2 text-red-600" />
          <div className="flex-1">
            <div className="text-sm">PDF</div>
            <div className="text-[10px] text-muted-foreground">Imprimible / compartible</div>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
